import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Repeat, Shuffle, Heart, Download } from 'lucide-react';

interface AudioPlayerProps {
  src: string;
  title: string;
  artist: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, title, artist }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => {
      if (isRepeat) {
        audio.currentTime = 0;
        audio.play();
      } else {
        setIsPlaying(false);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isRepeat]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      try {
        await audio.play();
      } catch (err) {
        console.error('Error playing audio:', err);
      }
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const skip = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + seconds));
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <audio ref={audioRef} src={src} preload="metadata" />
      
      <div className="w-full max-w-sm md:max-w-md mx-auto text-white p-4 md:p-0">
        {/* Album Art Placeholder */}
        <div className="relative mb-6 md:mb-8">
          <div className="w-48 h-48 md:w-80 md:h-80 mx-auto bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl shadow-2xl flex items-center justify-center mb-4 md:mb-6">
            <div className="text-center">
              <div className="w-12 h-12 md:w-20 md:h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                <Volume2 size={20} className="md:w-8 md:h-8" />
              </div>
              <p className="text-white text-opacity-80 text-sm md:text-base">Artwork</p>
            </div>
          </div>
          
          {/* Floating Play Button */}
          <button
            onClick={togglePlay}
            className="absolute bottom-2 right-2 md:bottom-4 md:right-4 w-12 h-12 md:w-16 md:h-16 bg-white text-purple-600 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
          >
            {isPlaying ? <Pause size={16} className="md:w-6 md:h-6" /> : <Play size={16} className="md:w-6 md:h-6" />}
          </button>
        </div>

        {/* Track Info */}
        <div className="text-center mb-4 md:mb-6">
          <h2 className="text-lg md:text-2xl font-bold mb-1 md:mb-2">{title}</h2>
          <p className="text-white text-opacity-70 text-sm md:text-lg">{artist}</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-4 md:mb-6">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-1 md:h-2 bg-white bg-opacity-20 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="flex justify-between text-xs md:text-sm text-white text-opacity-70 mt-1 md:mt-2">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 md:gap-6 mb-4 md:mb-6">
          <button
            onClick={() => setIsShuffle(!isShuffle)}
            className={`p-2 rounded-full transition-colors ${
              isShuffle ? 'text-purple-300' : 'text-white text-opacity-50 hover:text-white'
            }`}
          >
            <Shuffle size={16} className="md:w-5 md:h-5" />
          </button>
          
          <button
            onClick={() => skip(-10)}
            className="p-3 hover:bg-white hover:bg-opacity-10 rounded-full transition-colors"
          >
            <SkipBack size={18} className="md:w-6 md:h-6" />
          </button>
          
          <button
            onClick={togglePlay}
            className="w-12 h-12 md:w-14 md:h-14 bg-white text-purple-600 rounded-full flex items-center justify-center hover:scale-105 transition-transform"
          >
            {isPlaying ? <Pause size={20} className="md:w-7 md:h-7" /> : <Play size={20} className="md:w-7 md:h-7" />}
          </button>
          
          <button
            onClick={() => skip(10)}
            className="p-3 hover:bg-white hover:bg-opacity-10 rounded-full transition-colors"
          >
            <SkipForward size={18} className="md:w-6 md:h-6" />
          </button>
          
          <button
            onClick={() => setIsRepeat(!isRepeat)}
            className={`p-2 rounded-full transition-colors ${
              isRepeat ? 'text-purple-300' : 'text-white text-opacity-50 hover:text-white'
            }`}
          >
            <Repeat size={16} className="md:w-5 md:h-5" />
          </button>
        </div>

        {/* Volume Control */}
        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={toggleMute}
            className="p-2 hover:bg-white hover:bg-opacity-10 rounded-full transition-colors"
          >
            {isMuted ? <VolumeX size={16} className="md:w-5 md:h-5" /> : <Volume2 size={16} className="md:w-5 md:h-5" />}
          </button>
          
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="flex-1 h-1 bg-white bg-opacity-20 rounded-lg appearance-none cursor-pointer"
          />
          
          <div className="flex gap-1 md:gap-2">
            <button className="p-2 hover:bg-white hover:bg-opacity-10 rounded-full transition-colors">
              <Heart size={16} className="md:w-5 md:h-5" />
            </button>
            
            <button
              onClick={() => window.open(src, '_blank')}
              className="p-2 hover:bg-white hover:bg-opacity-10 rounded-full transition-colors"
            >
              <Download size={16} className="md:w-5 md:h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;